import React, { useState } from 'react';
import { formatTime12h } from '../shared/availability';
import { usePermission } from '../hooks/usePermission';
import { ProgressBar } from '../ui/ProgressBar';
import { useNavigate } from 'react-router-dom';
import {
  User, Award, TrendingUp, DollarSign, Users, Target, Calendar,
  BarChart3, Trophy, Zap, Star, Medal, Crown, Flame, Clock,
  ChevronRight, Gift, Heart,
  MessageSquare, AlertCircle,
  Shield, BookOpen, CreditCard, UserCheck,
  CheckCircle, XCircle,
  Database
} from 'lucide-react';
import CommunicationCenter from './modules/CommunicationCenter';
import BarberPerformanceCharts from './BarberPerformanceCharts';
import BarberGoalsManagement from './BarberGoalsManagement';
import BPIManagementModal from './BPIManagementModal';
import { barberDataService } from '../services/barberDataService';
import { locationService } from '../services/locationService';
import { appointmentService } from '../services/appointmentService';
import { clientDataService } from '../services/clientDataService';
import { BPIService, BPIMetrics } from '../utils/bpiService';
import { BPIScoreCard } from './ai/BPIScoreCard';
import { GrowthScoreCard } from './growth/GrowthScoreCard';
import { GrowthRecomputeButton } from './growth/GrowthRecomputeButton';
import { maybeAutoRecomputeGrowth } from '../services/autoGrowthRecompute';
import { GrowthCoachingCard } from './growth/GrowthCoachingCard';
import { GrowthEarningsCard } from './growth/GrowthEarningsCard';
import { GrowthProjectionCard } from './growth/GrowthProjectionCard';
import { GrowthPayWeekCard } from './growth/GrowthPayWeekCard';
import { GrowthTrackGoals } from './growth/GrowthTrackGoals';
import aiSettings from '../services/AISettingsService';
import toast from 'react-hot-toast';
import CSVImportCard from './CSVImportCard';
import { ymd } from '../shared/dateUtils';
// Layer 5 (2026-08-04): the SAME commission helpers + rates payroll uses, so what a barber
// sees on their profile equals what payroll pays. BEHAVIOR-PIN: SLIK close-the-loop reporting.
import { calculateBarberPay, getDateRangeForPeriod, roundToTwo } from '../utils/reportCalculations';
import { loadFreshaHistory, sumDaysRange } from '../services/freshaHistoryService';
import { commissionForBarber, mergeCommissionPay, DEFAULT_COMMISSION_TIERS, CommissionTier as ShopCommissionTier } from '../shared/growth';
import { shopSettingsService } from '../services/shopSettingsService';
import { computeBpi, mergeBpiConfig, bpiRank, type BpiResult } from '../shared/bpi';
import { resolveForConfig } from '../services/bpiMetricsService';
import { loadBpiRecord } from '../services/bpiService';

interface BarberProfile {
  growth?: any; // Growth System snapshot — carried through the map so the growth cards render
  // Basic Information
  id: string;
  name: string;
  email: string;
  phone: string;
  photo?: string;
  role: 'Elite' | 'Expert' | 'Senior' | 'Advanced' | 'Intermediate' | 'Rising Star';
  experience: number; // years
  status: 'active' | 'inactive' | 'vacation' | 'training';

  // Credentials & Locations (2026-08-02 — captured in the Add-Barber wizard)
  licensePhoto?: string;
  licenseNumber?: string;
  licenseExpiry?: string;
  locationIds?: string[];
  hasPin?: boolean; // BEHAVIOR-PIN (2026-09-29): PIN presence flag; raw pin no longer readable

  // Performance Metrics
  monthlyRevenue: number;
  monthlyClients: number;
  monthlyRevenueGoal: number;
  monthlyClientGoal: number;
  avgTicket: string;
  ytdRevenue: number;
  ytdClients: number;

  // Advanced Metrics
  retentionRate: number;
  rebookingRate: number;
  productSalesRatio: number;
  averageServiceTime: number;
  clientSatisfactionScore: number;
  noShowRate: number;

  // Specialties & Skills
  specialties: string[];
  certifications: string[];
  trainingCompleted: string[];
  skillLevels: {
    technical: number;
    speed: number;
    sales: number;
    customerService: number;
    teamwork: number;
  };

  // Gamification
  level: number;
  points: number;
  digitalCoins: number;
  streak: number;
  achievements: Achievement[];
  badges: Badge[];
  activeChallenges: Challenge[];

  // Services & Products
  topServices: ServiceData[];
  productSales: ProductSale[];
  favoriteProducts: string[];

  // Client Relations
  totalClients: number;
  vipClients: number;
  newClients: number;
  lostClients: number;
  clientBirthdays: ClientBirthday[];
  upcomingAppointments: Appointment[];

  // Financial
  commission: number;
  commissionRate?: number; // For backwards compatibility
  tipsAverage: number;
  dailyCashFlow: number;
  weeklyEarnings: number;
  paymentMethodBreakdown: {
    cash: number;
    card: number;
    app: number;
  };

  // Sliding Scale Commission
  commissionTiers?: {
    enabled: boolean;
    tiers: Array<{
      minRevenue: number;
      maxRevenue: number;
      rate: number;
    }>;
  };
  currentTier?: {
    rate: number;
    minRevenue: number;
    maxRevenue: number;
  };

  // Schedule & Availability
  workingHours: WorkingHours;
  breakSchedule: string[];
  timeOffRequests: TimeOffRequest[];
  availability: number; // percentage
}

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  unlockedAt?: Date;
  progress?: number;
  target?: number;
}

interface Badge {
  id: string;
  name: string;
  icon: string;
  color: string;
  earnedAt: Date;
}

interface Challenge {
  id: string;
  name: string;
  type: 'individual' | 'team';
  progress: number;
  target: number;
  reward: number;
  endDate: Date;
}

interface ServiceData {
  name: string;
  count: number;
  revenue: number;
  avgDuration: number;
}

interface ProductSale {
  name: string;
  quantity: number;
  revenue: number;
  commission: number;
}

interface ClientBirthday {
  clientName: string;
  date: string;
  phone: string;
}

interface Appointment {
  id: string;
  clientName: string;
  service: string;
  time: string;
  status: string;
}

interface WorkingHours {
  monday: { start: string; end: string };
  tuesday: { start: string; end: string };
  wednesday: { start: string; end: string };
  thursday: { start: string; end: string };
  friday: { start: string; end: string };
  saturday: { start: string; end: string };
  sunday: { start: string; end: string };
}

interface TimeOffRequest {
  id: string;
  type: string;
  startDate: string;
  endDate: string;
  status: string;
}

// Comprehensive barber data with ALL capabilities (used as defaults)
const defaultBarbers: BarberProfile[] = [
    {
      id: 'barber_jose',
      name: 'Jose',
      email: 'jose@slikpos.com',
      phone: '(555) 123-4567',
      role: 'Elite',
      experience: 8,
      status: 'active',

      monthlyRevenue: Math.round(110475.43 / 9),
      monthlyClients: Math.round(2410 / 9),
      monthlyRevenueGoal: Math.round((110475.43 / 9) * 1.15),
      monthlyClientGoal: Math.round((2410 / 9) * 1.15),
      avgTicket: '45.85',
      ytdRevenue: 110475.43,
      ytdClients: 2410,

      retentionRate: 92,
      rebookingRate: 88,
      productSalesRatio: 18,
      averageServiceTime: 32,
      clientSatisfactionScore: 4.9,
      noShowRate: 3,

      specialties: ['Fade Specialist', 'Beard Sculpting', 'Hair Design', 'Color Services'],
      certifications: ['Master Barber', 'Color Specialist', 'Safety Certified'],
      trainingCompleted: ['Advanced Cutting', 'Client Relations', 'Product Knowledge'],
      skillLevels: {
        technical: 95,
        speed: 92,
        sales: 88,
        customerService: 94,
        teamwork: 90
      },

      level: 25,
      points: 15750,
      digitalCoins: 3200,
      streak: 30,
      achievements: [
        { id: '1', title: 'Revenue Master', description: 'Exceeded $100K YTD', icon: 'trophy', progress: 100, target: 100 },
        { id: '2', title: 'Client Magnet', description: '2000+ clients served', icon: 'users', progress: 2410, target: 2000 },
        { id: '3', title: 'Consistency King', description: '30-day streak', icon: 'flame', progress: 30, target: 30 }
      ],
      badges: [
        { id: '1', name: 'Elite Performer', icon: 'crown', color: 'purple', earnedAt: new Date() },
        { id: '2', name: 'Speed Demon', icon: 'zap', color: 'yellow', earnedAt: new Date() }
      ],
      activeChallenges: [
        { id: '1', name: 'December Revenue Goal', type: 'individual', progress: 8500, target: 15000, reward: 500, endDate: new Date('2024-12-31') },
        { id: '2', name: 'Team Client Goal', type: 'team', progress: 850, target: 1000, reward: 200, endDate: new Date('2024-12-31') }
      ],

      topServices: [
        { name: 'Premium Cut & Style', count: 890, revenue: 44500, avgDuration: 35 },
        { name: 'Beard Sculpting', count: 645, revenue: 25800, avgDuration: 25 },
        { name: 'Executive Package', count: 420, revenue: 33600, avgDuration: 50 }
      ],
      productSales: [
        { name: 'Premium Pomade', quantity: 45, revenue: 1350, commission: 270 },
        { name: 'Beard Oil', quantity: 38, revenue: 950, commission: 190 },
        { name: 'Hair Tonic', quantity: 25, revenue: 625, commission: 125 }
      ],
      favoriteProducts: ['Premium Pomade', 'Styling Clay', 'Beard Oil'],

      totalClients: 485,
      vipClients: 45,
      newClients: 28,
      lostClients: 5,
      clientBirthdays: [
        { clientName: 'John Smith', date: '2024-02-15', phone: '(555) 234-5678' },
        { clientName: 'Mike Johnson', date: '2024-02-18', phone: '(555) 345-6789' }
      ],
      upcomingAppointments: [
        { id: '1', clientName: 'Robert Brown', service: 'Premium Cut', time: '10:00 AM', status: 'confirmed' },
        { id: '2', clientName: 'David Wilson', service: 'Beard Trim', time: '11:00 AM', status: 'confirmed' }
      ],

      commission: 35,
      tipsAverage: 12.50,
      dailyCashFlow: 550,
      weeklyEarnings: 2750,
      paymentMethodBreakdown: {
        cash: 30,
        card: 55,
        app: 15
      },

      workingHours: {
        monday: { start: '9:00', end: '18:00' },
        tuesday: { start: '9:00', end: '18:00' },
        wednesday: { start: '9:00', end: '18:00' },
        thursday: { start: '9:00', end: '20:00' },
        friday: { start: '9:00', end: '20:00' },
        saturday: { start: '8:00', end: '17:00' },
        sunday: { start: 'Closed', end: 'Closed' }
      },
      breakSchedule: ['12:00-13:00'],
      timeOffRequests: [
        { id: '1', type: 'vacation', startDate: '2024-03-15', endDate: '2024-03-20', status: 'pending' }
      ],
      availability: 92
    },
    // James
    {
      id: 'barber_james',
      name: 'James',
      email: 'james@slikpos.com',
      phone: '(555) 234-5678',
      role: 'Senior',
      experience: 6,
      status: 'active',

      monthlyRevenue: Math.round(95000 / 9),
      monthlyClients: Math.round(2100 / 9),
      monthlyRevenueGoal: Math.round((95000 / 9) * 1.15),
      monthlyClientGoal: Math.round((2100 / 9) * 1.15),
      avgTicket: '42.50',
      ytdRevenue: 95000,
      ytdClients: 2100,

      retentionRate: 88,
      rebookingRate: 85,
      productSalesRatio: 15,
      averageServiceTime: 35,
      clientSatisfactionScore: 4.8,
      noShowRate: 4,

      specialties: ['Classic Cuts', 'Beard Grooming', 'Hot Shaves'],
      certifications: ['Senior Barber', 'Safety Certified'],
      trainingCompleted: ['Client Relations', 'Product Knowledge'],
      skillLevels: {
        technical: 90,
        speed: 88,
        sales: 85,
        customerService: 92,
        teamwork: 88
      },

      level: 22,
      points: 13200,
      digitalCoins: 2800,
      streak: 25,
      achievements: [
        { id: '1', title: 'Consistency Pro', description: '25-day streak', icon: 'flame', progress: 25, target: 25 },
        { id: '2', title: 'Client Favorite', description: '2000+ clients served', icon: 'heart', progress: 2100, target: 2000 }
      ],
      badges: [
        { id: '1', name: 'Senior Expert', icon: 'star', color: 'blue', earnedAt: new Date() }
      ],
      activeChallenges: [
        { id: '1', name: 'December Revenue Goal', type: 'individual', progress: 7800, target: 14000, reward: 450, endDate: new Date('2024-12-31') }
      ],

      topServices: [
        { name: 'Classic Cut', count: 142, revenue: 4970, avgDuration: 30 },
        { name: 'Beard Trim', count: 98, revenue: 1960, avgDuration: 20 }
      ],
      productSales: [
        { name: 'Beard Oil', quantity: 24, revenue: 600, commission: 120 }
      ],
      favoriteProducts: ['Beard Oil', 'Pomade'],

      totalClients: 312,
      vipClients: 45,
      newClients: 28,
      lostClients: 5,
      clientBirthdays: [],
      upcomingAppointments: [],

      commission: 3500,
      tipsAverage: 8.50,
      dailyCashFlow: 450,
      weeklyEarnings: 2250,
      paymentMethodBreakdown: { cash: 30, card: 55, app: 15 },

      workingHours: {
        monday: { start: '9:00', end: '19:00' },
        tuesday: { start: '9:00', end: '19:00' },
        wednesday: { start: '9:00', end: '19:00' },
        thursday: { start: '9:00', end: '20:00' },
        friday: { start: '9:00', end: '20:00' },
        saturday: { start: '8:00', end: '17:00' },
        sunday: { start: 'Closed', end: 'Closed' }
      },
      breakSchedule: ['12:30-13:30'],
      timeOffRequests: [],
      availability: 90
    },
    // Kelly
    {
      id: 'barber_kelly',
      name: 'Kelly',
      email: 'kelly@slikpos.com',
      phone: '(555) 345-6789',
      role: 'Senior',
      experience: 5,
      status: 'active',

      monthlyRevenue: Math.round(87000 / 9),
      monthlyClients: Math.round(1950 / 9),
      monthlyRevenueGoal: Math.round((87000 / 9) * 1.15),
      monthlyClientGoal: Math.round((1950 / 9) * 1.15),
      avgTicket: '40.25',
      ytdRevenue: 87000,
      ytdClients: 1950,

      retentionRate: 85,
      rebookingRate: 82,
      productSalesRatio: 12,
      averageServiceTime: 33,
      clientSatisfactionScore: 4.7,
      noShowRate: 5,

      specialties: ['Modern Cuts', 'Hair Design', 'Consultations'],
      certifications: ['Senior Barber', 'Color Certified'],
      trainingCompleted: ['Advanced Cutting', 'Sales Training'],
      skillLevels: {
        technical: 88,
        speed: 85,
        sales: 82,
        customerService: 90,
        teamwork: 92
      },

      level: 20,
      points: 11500,
      digitalCoins: 2400,
      streak: 20,
      achievements: [
        { id: '1', title: 'Team Player', description: 'Top teamwork score', icon: 'users', progress: 100, target: 100 }
      ],
      badges: [
        { id: '1', name: 'Rising Star', icon: 'trending-up', color: 'green', earnedAt: new Date() }
      ],
      activeChallenges: [
        { id: '1', name: 'December Revenue Goal', type: 'individual', progress: 7000, target: 13000, reward: 400, endDate: new Date('2024-12-31') }
      ],

      topServices: [
        { name: 'Modern Cut', count: 130, revenue: 4550, avgDuration: 35 },
        { name: 'Hair Design', count: 85, revenue: 3400, avgDuration: 45 }
      ],
      productSales: [
        { name: 'Hair Gel', quantity: 20, revenue: 400, commission: 80 }
      ],
      favoriteProducts: ['Hair Gel', 'Texture Spray'],

      totalClients: 285,
      vipClients: 38,
      newClients: 22,
      lostClients: 8,
      clientBirthdays: [],
      upcomingAppointments: [],

      commission: 3200,
      tipsAverage: 7.75,
      dailyCashFlow: 420,
      weeklyEarnings: 2100,
      paymentMethodBreakdown: { cash: 25, card: 60, app: 15 },

      workingHours: {
        monday: { start: '10:00', end: '19:00' },
        tuesday: { start: '10:00', end: '19:00' },
        wednesday: { start: '10:00', end: '19:00' },
        thursday: { start: '10:00', end: '20:00' },
        friday: { start: '10:00', end: '20:00' },
        saturday: { start: '9:00', end: '17:00' },
        sunday: { start: 'Closed', end: 'Closed' }
      },
      breakSchedule: ['13:00-14:00'],
      timeOffRequests: [],
      availability: 88
    },
    // Marissa Frasso
    {
      id: 'barber_marissa',
      name: 'Marissa Frasso',
      email: 'marissa@slikpos.com',
      phone: '(555) 456-7890',
      role: 'Advanced',
      experience: 4,
      status: 'active',

      monthlyRevenue: Math.round(78000 / 9),
      monthlyClients: Math.round(1800 / 9),
      monthlyRevenueGoal: Math.round((78000 / 9) * 1.15),
      monthlyClientGoal: Math.round((1800 / 9) * 1.15),
      avgTicket: '38.50',
      ytdRevenue: 78000,
      ytdClients: 1800,

      retentionRate: 82,
      rebookingRate: 80,
      productSalesRatio: 14,
      averageServiceTime: 30,
      clientSatisfactionScore: 4.8,
      noShowRate: 3,

      specialties: ['Creative Colors', 'Women\'s Cuts', 'Styling'],
      certifications: ['Color Specialist', 'Styling Expert'],
      trainingCompleted: ['Color Theory', 'Client Relations'],
      skillLevels: {
        technical: 85,
        speed: 90,
        sales: 80,
        customerService: 93,
        teamwork: 89
      },

      level: 18,
      points: 10200,
      digitalCoins: 2100,
      streak: 15,
      achievements: [
        { id: '1', title: 'Color Expert', description: 'Master of color services', icon: 'palette', progress: 100, target: 100 }
      ],
      badges: [
        { id: '1', name: 'Creative Artist', icon: 'brush', color: 'pink', earnedAt: new Date() }
      ],
      activeChallenges: [
        { id: '1', name: 'December Revenue Goal', type: 'individual', progress: 6500, target: 12000, reward: 350, endDate: new Date('2024-12-31') }
      ],

      topServices: [
        { name: 'Color Service', count: 95, revenue: 4750, avgDuration: 60 },
        { name: 'Women\'s Cut', count: 120, revenue: 3600, avgDuration: 40 }
      ],
      productSales: [
        { name: 'Color Protect Shampoo', quantity: 30, revenue: 750, commission: 150 }
      ],
      favoriteProducts: ['Color Protect', 'Styling Cream'],

      totalClients: 260,
      vipClients: 32,
      newClients: 30,
      lostClients: 4,
      clientBirthdays: [],
      upcomingAppointments: [],

      commission: 2900,
      tipsAverage: 9.25,
      dailyCashFlow: 390,
      weeklyEarnings: 1950,
      paymentMethodBreakdown: { cash: 20, card: 65, app: 15 },

      workingHours: {
        monday: { start: '10:00', end: '18:00' },
        tuesday: { start: '10:00', end: '18:00' },
        wednesday: { start: '10:00', end: '18:00' },
        thursday: { start: '10:00', end: '19:00' },
        friday: { start: '10:00', end: '19:00' },
        saturday: { start: '9:00', end: '16:00' },
        sunday: { start: 'Closed', end: 'Closed' }
      },
      breakSchedule: ['13:00-13:30'],
      timeOffRequests: [],
      availability: 85
    }
  ];

interface ComprehensiveBarberProfileProps {
  initialBarberId?: string;
}

// DESIGN-LATER (2026-08-02): the gamification layer (achievements / challenges / SLIK coins /
// streak / points) is HIDDEN via this flag — NOT deleted. Jose wants a real motivation system
// designed later; flip to true (and wire real data) when that's built. Ref: memory SLIK-POS Layer 5.
const SHOW_GAMIFICATION = false;

const ComprehensiveBarberProfile: React.FC<ComprehensiveBarberProfileProps> = ({ initialBarberId }) => {
  const navigate = useNavigate();

  // Sliding-scale commission tiers now come from the shop's real commissionPay settings (shopTiers),
  // resolved per-period via commissionForBarber — see the Commission Structure card below. The old
  // hardcoded defaultCommissionTiers + calculateCurrentTier were removed 2026-09-27 (they showed
  // phantom $0–3000/… tiers that didn't match Settings or payroll).

  const [activeTab, setActiveTab] = useState<'overview' | 'performance' | 'clients' | 'finance' | 'communication' | 'training' | 'goals' | 'import'>('overview');
  // A barber viewing their OWN profile sees ONLY Overview + Finance (Jose 2026-09-27); managers see all.
  const { isManager, isOwner } = usePermission();
  const [selectedBarber, setSelectedBarber] = useState<number>(0);
  const [showBPIModal, setShowBPIModal] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [barbers, setBarbers] = useState<BarberProfile[]>([]);
  const [growthRefresh, setGrowthRefresh] = useState(0); // bump to re-load barbers after Recompute Growth
  const [showSchedule, setShowSchedule] = useState(false);
  const [showSLIKCoins, setShowSLIKCoins] = useState(false);
  const [aiSettingsState, setAiSettingsState] = useState(aiSettings.getSettings());
  const [locations, setLocations] = useState<any[]>([]); // resolve locationIds → names for Works-At (2026-08-02)
  // Layer 5 (2026-08-04): period toggle for the barber's own earnings/services/products report.
  const [reportPeriod, setReportPeriod] = useState<'today' | 'this-week' | 'this-month' | 'this-year'>('today');
  // Warm shop settings so the commission-rate lookups below return the real rate (not a default).
  const [settingsReady, setSettingsReady] = useState(false);
  // WEEK START for "This Week" (Jose 2026-10-26 · CORRECTED 2026-10-27): the Finance tab was defaulting to
  // a MONDAY-start week, so on a Monday "This Week" showed only today and dropped the rest of the week.
  // THE REAL PAY WEEK = `commissionPay.payWeekStartDay` (default 3 = WEDNESDAY, Wed→Tue) — the SAME source
  // PayrollReport.tsx (currentPayWeekStart) and the GrowthPayWeekCard use. (reporting.payroll.weekEndingDay
  // is stale/dead config — do NOT use it; keying off it wrongly gave Sunday.) Default 3; set from settings.
  const [weekStartDay, setWeekStartDay] = useState<0 | 1 | 2 | 3 | 4 | 5 | 6>(3);
  // Shop sliding-scale tiers so per-barber earnings use the SAME scale as payroll. Jose 2026-09-01.
  const [shopTiers, setShopTiers] = useState<ShopCommissionTier[]>(DEFAULT_COMMISSION_TIERS);
  React.useEffect(() => {
    (async () => {
      try {
        const pay = mergeCommissionPay(await shopSettingsService.getSettingsByType('commissionPay'));
        if (pay?.tiers?.length) setShopTiers(pay.tiers);
        // Pay-week start day — the ONE source of truth payroll + growth use. Keeps "This Week" == payroll week.
        const wsd = (pay as any)?.payWeekStartDay;
        if (typeof wsd === 'number' && wsd >= 0 && wsd <= 6) setWeekStartDay(wsd as 0|1|2|3|4|5|6);
      } catch { /* keep default scale */ }
    })();
  }, []);

  // PAY-VAULT RE-RENDER (Jose 2026-10-27): commissionMode/commissionRate live in the operator-locked
  // `barberPrivate` vault and merge into barberDataService ASYNCHRONOUSLY. This profile computed its
  // commission (line ~965 via `commissionForBarber(getBarberById(...), …)`) on its FIRST render — before
  // the merge landed — so `commissionMode` was undefined and the resolver fell back to the SLIDING scale
  // (showed 45% for a flat-50% barber). PAYROLL was always correct (it reads the merged record); only this
  // display was wrong. Subscribe to barberDataService so we RE-RENDER when the vault merges → getBarberById()
  // then returns the real `commissionMode:'flat'` → the profile shows 50%, matching payroll. Display-only.
  const [, _bumpPay] = useState(0);
  React.useEffect(() => {
    const unsub = barberDataService.subscribe(() => _bumpPay((n) => n + 1));
    return unsub;
  }, []);

  // BPI (rework 2026-09-27): the real score for the CURRENT barber via the new engine (config from
  // Settings → Growth & Bonus, computed values from live data, manual values from bpiData). Async →
  // held in state; recomputes when the barber changes or after a save (bpiRefresh).
  const [bpiResult, setBpiResult] = useState<BpiResult | null>(null);
  const [bpiRefresh, setBpiRefresh] = useState(0);
  const _bpiBarberId = barbers[selectedBarber]?.id;
  React.useEffect(() => {
    let alive = true;
    (async () => {
      if (!_bpiBarberId) { setBpiResult(null); return; }
      try {
        const cfg = mergeBpiConfig(await shopSettingsService.getSettingsByType('bpiConfig'));
        const barber = barberDataService.getBarberById(_bpiBarberId) as any;
        const appts = appointmentService.getAppointmentsByBarber(_bpiBarberId);
        const computed = barber ? resolveForConfig(cfg, barber, appts) : {};
        const rec = await loadBpiRecord(_bpiBarberId);
        const manual: Record<string, number> = {};
        for (const p of cfg.params) if (p.kind === 'manual') {
          const v = rec?.manualValues?.[p.key];
          manual[p.key] = typeof v === 'number' ? v : (typeof p.manualDefault === 'number' ? p.manualDefault : 60);
        }
        const res = computeBpi(cfg, computed, manual, rec?.adjustment || 0);
        if (alive) setBpiResult(res);
      } catch { if (alive) setBpiResult(null); }
    })();
    return () => { alive = false; };
  }, [_bpiBarberId, bpiRefresh]);

  React.useEffect(() => {
    locationService.getAllLocations().then(setLocations).catch(() => {});
  }, []);

  React.useEffect(() => {
    shopSettingsService.getSettings().then(() => setSettingsReady(true)).catch(() => setSettingsReady(true));
  }, []);

  // Load barbers from service on mount
  React.useEffect(() => {
    const loadBarbers = async () => {
      console.log('📊 ComprehensiveBarberProfile: Loading barbers from Firebase...');
      const barbersData = await barberDataService.getAllBarbers();
      // Reconciled YTD + current-month revenue from freshaHistory months (the barber-doc monthlyRevenue
      // reads $0 for Fresha-era barbers → "$0K YTD"). Services + products net; falls back to the estimate.
      // (Jose 2026-10-18 — "audit the Finance connections")
      const _fhList = await loadFreshaHistory().catch(() => [] as any[]);
      const _fhByBarber = new Map<string, any>(_fhList.map((fb: any) => [fb.barberId, fb]));
      const _curYM = new Date().toISOString().slice(0, 7);
      const _curYear = _curYM.slice(0, 4);
      const _fhRev = (bid: string) => {
        const fb = _fhByBarber.get(bid);
        let ytd = 0, month = 0;
        if (fb?.months) for (const [ym, m] of Object.entries<any>(fb.months)) {
          const net = (m?.svcNet || 0) + (m?.prodNet || 0);
          if (ym.startsWith(_curYear)) ytd += net;
          if (ym === _curYM) month = net;
        }
        return { ytd, month };
      };
      // Map Firebase barbers to UI format
      if (barbersData.length > 0) {
        const mappedBarbers = barbersData.map(b => ({
          // ✅ USE REAL FIREBASE DATA - NO HARDCODED DEFAULTS
          id: b.id,
          name: `${b.firstName} ${b.lastName}`,
          email: b.email,
          phone: b.phone,
          photo: b.photo || '👤',
          role: b.careerStage as any, // Use careerStage from Firebase
          experience: b.experience || 0,
          status: b.status as any || 'active',

          // Performance metrics — reconciled freshaHistory first (services+products net), then the
          // barber-doc estimate. Fixes "$0K YTD Revenue" for Fresha-era barbers like Diana.
          monthlyRevenue: _fhRev(b.id).month || b.monthlyRevenue || 0,
          ytdRevenue: _fhRev(b.id).ytd || (b.monthlyRevenue * 9) || 0,
          monthlyClients: Math.round(b.totalClients / 9) || 0,
          ytdClients: b.totalClients || 0,
          avgTicket: b.monthlyRevenue && b.totalClients ? ((b.monthlyRevenue / (b.totalClients / 9)) || 0).toFixed(2) : '0',
          retentionRate: b.totalClients > 0 ? Math.round((b.returningClients / b.totalClients) * 100) : 0,
          rebookingRate: b.totalClients > 0 ? Math.round((b.returningClients / b.totalClients) * 100) : 0,
          clientSatisfactionScore: b.avgRating || 0,
          avgServiceTime: b.avgServiceTime || 0,
          averageServiceTime: b.avgServiceTime || 0, // Duplicate field expected by interface
          noShowRate: 0, // TODO: Calculate from appointments
          productSalesRatio: 0, // TODO: Calculate from product sales vs service revenue

          // Skills & certifications from Firebase
          certifications: b.certifications || [],
          specialties: b.skills || [], // Use actual skills from Firebase
          trainingCompleted: [], // Not in Firebase yet

          // Skill levels - calculate from performance data
          skillLevels: {
            technical: Math.min(100, Math.round(b.experience * 10) || 50),
            speed: b.avgServiceTime ? Math.round((60 - b.avgServiceTime) * 2) : 50,
            sales: Math.min(100, Math.round((b.monthlyRevenue / 100)) || 50),
            customerService: Math.round((b.avgRating / 5) * 100) || 50,
            teamwork: 75 // Default until we have team metrics
          },

          // Career & compensation
          commission: b.commissionRate || 50,
          commissionRate: b.commissionRate,
          commissionTiers: b.commissionTiers,
          currentTier: b.currentTier,
          tipsAverage: 0, // Will calculate from appointments

          // Goals
          monthlyRevenueGoal: Math.round(b.monthlyRevenue * 1.15) || 1000,
          monthlyClientGoal: Math.round((b.totalClients / 9) * 1.15) || 50,

          // Gamification - not in Firebase yet
          level: Math.floor(b.experience / 2) || 1,
          points: Math.round(b.monthlyRevenue / 10) || 0,
          digitalCoins: Math.round(b.monthlyRevenue / 100) || 0,
          streak: 0,
          achievements: [],
          badges: [],
          activeChallenges: [],

          // Services & products - will query from appointments
          topServices: [], // Will load from reportingDataService
          productSales: [],
          favoriteProducts: [],

          // Clients - use Firebase data
          totalClients: b.totalClients || 0,
          vipClients: 0, // TODO: Query from clients collection
          newClients: 0, // TODO: Calculate from appointments
          lostClients: 0, // TODO: Calculate from appointments
          clientBirthdays: [],
          upcomingAppointments: [],

          // Financial
          dailyCashFlow: Math.round(b.monthlyRevenue / 22) || 0,
          weeklyEarnings: Math.round(b.monthlyRevenue / 4) || 0,
          paymentMethodBreakdown: { cash: 0, card: 0, app: 0 }, // TODO: Calculate from appointments

          // Schedule from Firebase
          workingHours: b.availability ? {
            monday: { start: b.availability.monday.start, end: b.availability.monday.end },
            tuesday: { start: b.availability.tuesday.start, end: b.availability.tuesday.end },
            wednesday: { start: b.availability.wednesday.start, end: b.availability.wednesday.end },
            thursday: { start: b.availability.thursday.start, end: b.availability.thursday.end },
            friday: { start: b.availability.friday.start, end: b.availability.friday.end },
            saturday: { start: b.availability.saturday.start, end: b.availability.saturday.end },
            sunday: { start: b.availability.sunday.start, end: b.availability.sunday.end }
          } : {
            monday: { start: '09:00', end: '17:00' },
            tuesday: { start: '09:00', end: '17:00' },
            wednesday: { start: '09:00', end: '17:00' },
            thursday: { start: '09:00', end: '17:00' },
            friday: { start: '09:00', end: '17:00' },
            saturday: { start: '09:00', end: '17:00' },
            sunday: { start: 'Closed', end: 'Closed' }
          },
          // 🪪 License + 📍 locations + PIN — real wizard data (Layer 1 wiring 2026-08-02)
          licensePhoto: b.licensePhoto || '',
          licenseNumber: b.licenseNumber || '',
          licenseExpiry: b.licenseExpiry || '',
          locationIds: b.locationIds || [],
          hasPin: !!(b as any).hasPinLogin, // BEHAVIOR-PIN: raw pin no longer on the barber doc
          breakSchedule: [],
          timeOffRequests: [],
          availability: b.availability ? Object.values(b.availability).filter(day => day.available).length / 7 * 100 : 100,
          growth: (b as any).growth, // Growth System snapshot → powers the growth cards (was dropped by this map)
        }));
        setBarbers(mappedBarbers);
        console.log(`✅ Loaded ${mappedBarbers.length} barbers from Firebase with REAL data`);

        // If initialBarberId is provided, find and select that barber
        if (initialBarberId) {
          const barberIndex = mappedBarbers.findIndex(b => b.id === initialBarberId);
          if (barberIndex !== -1) {
            setSelectedBarber(barberIndex);
            console.log(`✅ Selected barber at index ${barberIndex}: ${mappedBarbers[barberIndex].name}`);
          } else {
            console.warn(`⚠️ Barber with ID ${initialBarberId} not found, defaulting to first barber`);
          }
        }
      } else {
        // Honest (Layer 2, 2026-08-02): NO silent fall-through to sample/mock barbers.
        console.warn('No barbers returned from Firebase — showing an empty state, not sample data.');
        setBarbers([]);
      }
    };
    loadBarbers();
  }, [initialBarberId, growthRefresh]);

  // Auto-recompute Growth once/day (no manual button needed). Runs in the SAME warm-cache context the
  // manual button uses; throttled shop-wide to ~once/20h; bumps the refresh so new numbers show. (Jose 2026-10-11)
  React.useEffect(() => {
    let cancelled = false;
    maybeAutoRecomputeGrowth().then(ran => { if (ran && !cancelled) setGrowthRefresh(x => x + 1); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for AI settings changes
  React.useEffect(() => {
    const handleSettingsChange = () => {
      setAiSettingsState(aiSettings.getSettings());
    };
    aiSettings.on('settingsChanged', handleSettingsChange);
    return () => {
      aiSettings.off('settingsChanged', handleSettingsChange);
    };
  }, []);

  // Reconciled freshaHistory for THIS barber + period, so the pay-week tips/revenue match Payroll/Reports.
  // The appointment records carry no tipAmount for Fresha-imported visits (tips live in freshaHistory), so
  // reading tips straight off appointments showed $0. We blend: Fresha for its covered days + live SLIK
  // appointments for uncovered days (same spine as getPayrollReport). (Jose 2026-10-18 — "Diana's tips 0")
  const [fhPeriod, setFhPeriod] = useState<{ svc: number; tips: number; covered: Set<string> }>({ svc: 0, tips: 0, covered: new Set() });
  React.useEffect(() => {
    let alive = true;
    const bid = barbers[selectedBarber]?.id;
    if (!bid) { setFhPeriod({ svc: 0, tips: 0, covered: new Set() }); return; }
    const per = reportPeriod === 'today' ? null : getDateRangeForPeriod({ period: reportPeriod, weekStartDay });
    const ps = per ? ymd(per.startDate) : ymd();
    const pe = per ? ymd(per.endDate) : ymd();
    loadFreshaHistory().then(list => {
      if (!alive) return;
      const fb = list.find(x => x.barberId === bid);
      if (!fb) { setFhPeriod({ svc: 0, tips: 0, covered: new Set() }); return; }
      const s = sumDaysRange(fb, ps, pe);
      const covered = new Set<string>();
      for (const iso of Object.keys(fb.days || {})) if (iso >= ps && iso <= pe) covered.add(iso);
      setFhPeriod({ svc: s.svcNet, tips: s.tips, covered });
    }).catch(() => { if (alive) setFhPeriod({ svc: 0, tips: 0, covered: new Set() }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barbers, selectedBarber, reportPeriod, weekStartDay]);

  const currentBarber = barbers[selectedBarber];

  // "Stay signed in" toggle (Jose 2026-10-19): owner/manager opt-out of the PIN pad on their own device.
  // Writes staySignedIn + the derived lock flags; PinLockContext honors them (no wake/open/idle lock).
  const handleStaySignedInToggle = async (on: boolean) => {
    if (!currentBarber) return;
    try {
      await barberDataService.updateBarber(currentBarber.id, on
        ? { staySignedIn: true, requirePinOnWake: false, requirePinOnOpen: false, autoLogoutMinutes: 0 } as any
        : { staySignedIn: false, requirePinOnWake: true, requirePinOnOpen: true, autoLogoutMinutes: 30 } as any);
      toast.success(on ? '🔓 PIN pad off — stays signed in on their device' : '🔒 PIN pad on');
    } catch {
      toast.error('Could not update sign-in setting');
    }
  };

  // Honest empty state — never fall through to fake sample data (Layer 2, 2026-08-02).
  if (!currentBarber) {
    return (
      <div className="p-10 text-center text-gray-500 dark:text-gray-400">
        <p className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-1">Couldn't load this barber</p>
        <p className="text-sm">No profile data came back. Try refreshing the page, or check your connection.</p>
      </div>
    );
  }

  // Layer 3 (2026-08-02): REAL metrics computed from this barber's appointments (in-memory).
  const _appts = appointmentService.getAppointmentsByBarber(currentBarber.id);
  const _todayStr = ymd();
  const todaysAppointments = _appts
    .filter(a => a.date === _todayStr && !['cancelled', 'completed', 'paid', 'no-show'].includes(a.status))
    .sort((a, b) => (a.time || '').localeCompare(b.time || ''));
  const _finished = _appts.filter(a => ['completed', 'paid', 'no-show'].includes(a.status));
  const realNoShowRate = _finished.length
    ? Math.round((_finished.filter(a => a.status === 'no-show').length / _finished.length) * 100)
    : null;
  const _svc: Record<string, { name: string; count: number; revenue: number; totalDuration: number }> = {};
  _appts.filter(a => ['completed', 'paid'].includes(a.status)).forEach(a => {
    const s = (_svc[a.service] = _svc[a.service] || { name: a.service, count: 0, revenue: 0, totalDuration: 0 });
    s.count++; s.revenue += (a.price || 0); s.totalDuration += (a.duration || 0);
  });
  const realTopServices = Object.values(_svc)
    .map(s => ({ name: s.name, count: s.count, revenue: s.revenue, avgDuration: s.count ? Math.round(s.totalDuration / s.count) : 0 }))
    .sort((a, b) => b.count - a.count).slice(0, 5);

  // Layer 4 (2026-08-02): this barber's clients (served via appointments OR set as preferred),
  // for VIP / New / Lost / Model — same client records that power the People-page VIP/Model tabs.
  const _clientIds = new Set(_appts.map(a => a.clientId).filter(Boolean));
  const barberClients = clientDataService.getAllClients().filter(
    c => _clientIds.has(c.id) || c.preferredBarber === currentBarber.name
  );
  const _now = Date.now();
  const _DAY = 86400000;
  const realVipClients = barberClients.filter(c => c.membershipType === 'vip').length;
  const realModelClients = barberClients.filter(c => c.isModel || c.membershipType === 'model').length;
  const realNewClients = barberClients.filter(c => (c as any).createdAt && (_now - new Date((c as any).createdAt).getTime()) < 30 * _DAY).length;
  const realLostClients = barberClients.filter(c => c.lastVisit && (_now - new Date(c.lastVisit).getTime()) > 90 * _DAY).length;

  // Layer 3b (2026-08-02): checkout data lives ON the appointment (tipAmount, paymentMethod).
  const _paid = _appts.filter(a => a.status === 'completed' || a.status === 'paid');
  const _pm = { cash: 0, card: 0, app: 0 };
  _paid.forEach(a => { const m = a.paymentMethod; if (m === 'cash') _pm.cash++; else if (m === 'card') _pm.card++; else if (m) _pm.app++; });
  const _pmTot = _pm.cash + _pm.card + _pm.app;
  const realPaymentMix = _pmTot ? { cash: Math.round(_pm.cash / _pmTot * 100), card: Math.round(_pm.card / _pmTot * 100), app: Math.round(_pm.app / _pmTot * 100) } : null;

  // Layer 5 (2026-08-04): THIS barber's earnings + services + products for the selected period,
  // computed from the same completed appointments and run through the SAME commission helpers +
  // rates getPayrollReport uses → the number the barber sees here equals the number payroll pays.
  const _period = reportPeriod === 'today' ? null : getDateRangeForPeriod({ period: reportPeriod, weekStartDay });
  const _pStart = _period ? ymd(_period.startDate) : _todayStr;
  const _pEnd = _period ? ymd(_period.endDate) : _todayStr;
  const _periodPaid = _paid.filter(a => a.date >= _pStart && a.date <= _pEnd); // YYYY-MM-DD compares lexically
  const periodServices = _periodPaid.length;
  const periodClients = new Set(_periodPaid.map(a => a.clientId).filter(Boolean)).size;
  // BLEND (Jose 2026-10-18): reconciled Fresha for its covered days + live SLIK appts on uncovered days —
  // so tips (which Fresha imports never write onto the appointment) and service revenue match payroll.
  const _uncoveredPaid = _periodPaid.filter(a => !fhPeriod.covered.has(a.date));
  const periodServiceRevenue = roundToTwo(fhPeriod.svc + _uncoveredPaid.reduce((s, a) => s + (a.price || 0), 0));
  const periodTips = roundToTwo(fhPeriod.tips + _uncoveredPaid.reduce((s, a) => s + (a.tipAmount || 0), 0));
  // Aggregate products sold per product for the line-item breakdown (from the retail loop's productsSold[]).
  const _prodAgg: Record<string, { name: string; quantity: number; revenue: number }> = {};
  _periodPaid.forEach(a => (a.productsSold || []).forEach(p => {
    const key = p.productId || p.productName;
    const row = (_prodAgg[key] = _prodAgg[key] || { name: p.productName, quantity: 0, revenue: 0 });
    row.quantity += (p.quantity || 1);
    row.revenue += (p.total || 0);
  }));
  const periodProductRevenue = Object.values(_prodAgg).reduce((s, r) => s + r.revenue, 0);
  // Rates: exactly what payroll pulls. Fall back to the barber's own rate until settings load, so first paint isn't wrong.
  // Per-barber pay mode (flat vs shop sliding scale) — the SAME resolver payroll uses. Jose 2026-09-01.
  const _fullBarber = barberDataService.getBarberById(currentBarber.id);
  // PAY-READY GUARD (Jose 2026-10-27, belt-and-braces): commissionMode/commissionRate live in the locked
  // barberPrivate vault and merge ASYNC. Until merged, commissionForBarber can't know a barber is FLAT and
  // falls back to the sliding default — which would flash the WRONG rate for a flat barber. `commissionRate`
  // is present on EVERY barber (flat AND sliding) once the vault merges, so it's a reliable "pay loaded"
  // signal. While not ready, show "—" for the rate/commission/take-home instead of a possibly-wrong number.
  const _payReady = !!_fullBarber && (_fullBarber as any).commissionRate != null;
  const _svcComm = commissionForBarber(_fullBarber, periodServiceRevenue, shopTiers);
  const _svcRate = Math.round(_svcComm.rate * 100); // percent, for the "Commission (N% svc)" label
  const _rateStr = _payReady ? `${_svcRate}%` : '—'; // guarded rate label
  const _prodRate = settingsReady ? shopSettingsService.getProductCommissionRate() : 10;
  const _periodSvcComm = _svcComm.commission;
  const _periodProdComm = roundToTwo(periodProductRevenue * (_prodRate / 100));
  const periodCommission = roundToTwo(_periodSvcComm + _periodProdComm);
  const periodTotalPay = calculateBarberPay({ serviceCommission: _periodSvcComm, productCommission: _periodProdComm, tips: periodTips });
  const periodTotalRevenue = periodServiceRevenue + periodProductRevenue + periodTips;
  const periodAvgTicket = periodServices ? periodTotalRevenue / periodServices : 0;
  const periodProductLines = Object.values(_prodAgg)
    .map(r => ({ name: r.name, quantity: r.quantity, revenue: roundToTwo(r.revenue), commission: roundToTwo(r.revenue * _prodRate / 100) }))
    .sort((a, b) => b.revenue - a.revenue);
  const periodProductUnits = periodProductLines.reduce((s, r) => s + r.quantity, 0);
  const _periodLabel = reportPeriod === 'today' ? 'Today' : reportPeriod === 'this-week' ? 'This Week' : reportPeriod === 'this-month' ? 'This Month' : 'This Year';
  // The individual completed checkouts for the selected period — the "what did I actually do" worklog,
  // newest first. (Jose 2026-08-13: "show today's work — clients, products, services — one place.")
  const _worklog = [..._periodPaid].sort((a, b) => String(b.time || '').localeCompare(String(a.time || '')));
  const _money = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Overview "Commission Structure" card (Jose 2026-09-27): rewired OFF the mock barber.monthlyRevenue
  // ($32.78) + hardcoded defaultCommissionTiers ($0–3000/…) and ONTO the SAME real numbers payroll +
  // the Finance tab use — this period's completed-service revenue (periodServiceRevenue) run through the
  // shop's real tiers (shopTiers, from the commissionPay settings). _svcComm already resolved
  // flat-vs-sliding + rate + next-tier via commissionForBarber, so the card = the paycheck.
  const _commRevenue = periodServiceRevenue;
  const _commSliding = !_svcComm.flat; // shop sliding-scale vs a per-barber flat rate — the SAME call payroll makes
  const _commDisplayTiers = [...shopTiers].sort((a, b) => a.min - b.min).map((t, i, arr) => ({
    minRevenue: t.min,
    maxRevenue: i < arr.length - 1 ? arr[i + 1].min - 1 : Number.POSITIVE_INFINITY,
    rate: Math.round(t.rate * 100),
  }));
  const _commCurrentTier = [..._commDisplayTiers].reverse().find(t => _commRevenue >= t.minRevenue)
    || _commDisplayTiers[0]
    || { minRevenue: 0, maxRevenue: Number.POSITIVE_INFINITY, rate: _svcRate };
  const _commNextTier = _commDisplayTiers.find(t => t.minRevenue > _commCurrentTier.minRevenue) || null;

  // This-month REAL snapshot for the Overview Performance card (completed appts this calendar month) —
  // replaces the stale barber.monthlyRevenue ($32.78) mock. clientId linkage is thin, so count unique
  // clients by clientId||clientName. (Jose 2026-09-27)
  const _mDate = new Date(_now);
  const _mStart = ymd(new Date(_mDate.getFullYear(), _mDate.getMonth(), 1));
  const _thisMonthPaid = _paid.filter(a => a.date >= _mStart && a.date <= _todayStr);
  const _mRevenue = _thisMonthPaid.reduce((s, a) => s + (a.price || 0), 0);
  const _mServices = _thisMonthPaid.length;
  const _mClients = new Set(_thisMonthPaid.map(a => a.clientId || a.clientName).filter(Boolean)).size;

  // Calculate BPI for current barber and percentile ranking
  const calculateBPIAndPercentile = () => {
    // Calculate BPI scores for all barbers
    const barberScores = barbers.map(barber => {
      const metrics: BPIMetrics = {
        totalAppointments: barber.ytdClients,
        totalRevenue: barber.ytdRevenue,
        totalTips: barber.tipsAverage * barber.ytdClients,
        rebookRate: barber.rebookingRate,
        averageRating: barber.clientSatisfactionScore,
        punctualityScore: 95,
        employmentStartDate: new Date(Date.now() - (barber.experience * 365 * 24 * 60 * 60 * 1000)),
        specialtyCertifications: barber.certifications,
        teamworkScore: barber.skillLevels.teamwork / 20,
        upsellRate: barber.productSalesRatio || 0
      };
      const bpi = BPIService.calculateBPI(barber.id, metrics);
      return { barberId: barber.id, score: bpi.score };
    });

    // Sort by score descending
    barberScores.sort((a, b) => b.score - a.score);

    // Find current barber's rank
    const currentRank = barberScores.findIndex(b => b.barberId === currentBarber.id) + 1;
    const percentile = Math.round(((barbers.length - currentRank + 1) / barbers.length) * 100);

    // Get current barber's BPI
    const currentBPIMetrics: BPIMetrics = {
      totalAppointments: currentBarber.ytdClients,
      totalRevenue: currentBarber.ytdRevenue,
      totalTips: currentBarber.tipsAverage * currentBarber.ytdClients,
      rebookRate: currentBarber.rebookingRate,
      averageRating: currentBarber.clientSatisfactionScore,
      punctualityScore: 95,
      employmentStartDate: new Date(Date.now() - (currentBarber.experience * 365 * 24 * 60 * 60 * 1000)),
      specialtyCertifications: currentBarber.certifications,
      teamworkScore: currentBarber.skillLevels.teamwork / 20,
      upsellRate: currentBarber.productSalesRatio || 0
    };
    const currentBPI = BPIService.calculateBPI(currentBarber.id, currentBPIMetrics);

    return {
      bpi: currentBPI,
      rank: currentRank,
      percentile: percentile,
      totalBarbers: barbers.length
    };
  };

  const bpiData = calculateBPIAndPercentile();

  const getLevelStyle = (role: string) => {
    switch(role) {
      case 'Elite':
        return { gradient: 'from-purple-500 to-purple-700', icon: Crown, color: 'purple' };
      case 'Expert':
        return { gradient: 'from-indigo-500 to-indigo-700', icon: Medal, color: 'indigo' };
      case 'Senior':
        return { gradient: 'from-emerald-500 to-emerald-700', icon: Star, color: 'emerald' };
      case 'Advanced':
        return { gradient: 'from-blue-500 to-blue-700', icon: Award, color: 'blue' };
      default:
        return { gradient: 'from-gray-500 to-gray-700', icon: User, color: 'gray' };
    }
  };

  const levelStyle = getLevelStyle(currentBarber.role);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const LevelIcon = levelStyle.icon;

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const photoURL = await barberDataService.uploadPhoto(currentBarber.id, file);
      await barberDataService.updateBarber(currentBarber.id, { photo: photoURL });

      // Update local state immediately
      setBarbers(prev => prev.map(b =>
        b.id === currentBarber.id ? { ...b, photo: photoURL } : b
      ));

      toast.success('✅ Photo updated successfully!', { duration: 3000 });
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-800">
      {/* Header Section */}
      <div className={`bg-gradient-to-r ${levelStyle.gradient} text-white`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-8">
          {/* Back Button */}
          <button
            onClick={() => navigate('/people')}
            className="mb-4 flex items-center gap-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-sm"
          >
            <ChevronRight className="h-4 w-4 rotate-180" />
            Back to Staff
          </button>

          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex items-center">
              <div className="relative group">
                {currentBarber.photo && (currentBarber.photo.startsWith('http') || currentBarber.photo.startsWith('data:')) ? (
                  <img
                    src={currentBarber.photo}
                    alt={currentBarber.name}
                    className="w-20 h-20 rounded-full object-cover border-4 border-white/30"
                  />
                ) : (
                  <div className="w-20 h-20 bg-white/20 backdrop-blur rounded-full flex items-center justify-center text-4xl text-white font-bold border-4 border-white/30">
                    {currentBarber.name[0]}
                  </div>
                )}

                {/* Photo Upload Overlay */}
                <label className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    disabled={uploadingPhoto}
                  />
                  {uploadingPhoto ? (
                    <div className="text-white text-xs">Uploading...</div>
                  ) : (
                    <span className="text-white text-2xl">📸</span>
                  )}
                </label>

                {currentBarber.streak > 20 && (
                  <div className="absolute -top-2 -right-2 bg-orange-500 rounded-full p-2">
                    <Flame className="h-5 w-5 text-white" />
                  </div>
                )}
                <div className={`absolute -bottom-2 -right-2 bg-${currentBarber.status === 'active' ? 'green' : 'gray'}-500 rounded-full p-1.5 border-2 border-white`}>
                  <CheckCircle className="h-4 w-4 text-white" />
                </div>
              </div>
              <div className="ml-4 md:ml-6 flex-1">
                <h1 className="text-2xl md:text-3xl font-bold">{currentBarber.name}</h1>
                <p className="text-white/90 text-sm md:text-base truncate">{currentBarber.email}</p>
                <p className="text-white/90 text-sm md:text-base">{currentBarber.phone}</p>
                <div className="flex flex-wrap items-center mt-2 gap-2">
                  <span className="px-2 md:px-3 py-1 bg-white/20 backdrop-blur rounded-full text-xs md:text-sm font-medium">
                    {currentBarber.role}
                  </span>
                  <span className="px-2 md:px-3 py-1 bg-white/20 backdrop-blur rounded-full text-xs md:text-sm">
                    {currentBarber.experience}yr Exp
                  </span>
                  <span className="px-2 md:px-3 py-1 bg-green-500/80 rounded-full text-xs md:text-sm capitalize">
                    {currentBarber.status}
                  </span>
                </div>
              </div>
            </div>

            <div className="text-left md:text-right space-y-2 md:space-y-3">
              {/* BPI Score Badge (if AI enabled) */}
              {aiSettingsState.enabled && aiSettingsState.modules.barberScoring && (
                <div className="flex flex-col md:items-end">
                  <BPIScoreCard
                    score={bpiData.bpi.score}
                    size="md"
                    showLabel={true}
                  />
                  <div className="mt-1 text-xs md:text-sm text-white/90">
                    <span className="font-semibold">Rank #{bpiData.rank}</span> of {bpiData.totalBarbers}
                    <span className="mx-1 md:mx-2">•</span>
                    <span className="font-semibold">Top {100 - bpiData.percentile}%</span>
                  </div>
                  {/* Growth Score (Phase 5) — auto-computed; renders null until first recompute. */}
                  <div className="mt-2 flex flex-col md:items-end gap-1.5">
                    <GrowthScoreCard growth={(currentBarber as any).growth} />
                    <GrowthRecomputeButton onComplete={() => setGrowthRefresh(x => x + 1)} />
                  </div>
                </div>
              )}

              {/* Gamification header badges (Level/points/streak/coins) — DESIGN-LATER, hidden */}
              {SHOW_GAMIFICATION && (<>
              <div className="flex md:flex-col items-center md:items-end gap-2 md:gap-0">
                <p className="text-white/90 text-xs md:text-sm">Level {currentBarber.level}</p>
                <p className="text-xl md:text-3xl font-bold">{currentBarber.points.toLocaleString()} pts</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="px-2 md:px-3 py-1 bg-yellow-500/80 rounded-full flex items-center">
                  <Flame className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                  <span className="text-xs md:text-sm font-bold">{currentBarber.streak} days</span>
                </div>
                <div className="px-2 md:px-3 py-1 bg-white/20 rounded-full flex items-center">
                  <CreditCard className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                  <span className="text-xs md:text-sm font-bold">{currentBarber.digitalCoins}</span>
                </div>
              </div>
              </>)}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-4 md:mt-6">
            <div className="bg-white/10 backdrop-blur rounded-lg p-3 md:p-4">
              <DollarSign className="h-5 w-5 md:h-6 md:w-6 mb-1 md:mb-2 text-white/90" />
              <p className="text-lg md:text-2xl font-bold">${(currentBarber.ytdRevenue / 1000).toFixed(0)}K</p>
              <p className="text-xs md:text-sm text-white/90">YTD Revenue</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-3 md:p-4">
              <Users className="h-5 w-5 md:h-6 md:w-6 mb-1 md:mb-2 text-white/90" />
              <p className="text-lg md:text-2xl font-bold">{currentBarber.ytdClients.toLocaleString()}</p>
              <p className="text-xs md:text-sm text-white/90">YTD Clients</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-3 md:p-4">
              <Heart className="h-5 w-5 md:h-6 md:w-6 mb-1 md:mb-2 text-white/90" />
              <p className="text-lg md:text-2xl font-bold">{currentBarber.retentionRate}%</p>
              <p className="text-xs md:text-sm text-white/90">Retention</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-3 md:p-4">
              <Star className="h-5 w-5 md:h-6 md:w-6 mb-1 md:mb-2 text-white/90" />
              <p className="text-lg md:text-2xl font-bold">{currentBarber.clientSatisfactionScore}</p>
              <p className="text-xs md:text-sm text-white/90">Rating</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white dark:bg-gray-800 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex gap-2 overflow-x-auto pb-2 -mb-2 scrollbar-hide">
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3 },
              { id: 'performance', label: 'Performance', icon: TrendingUp },
              { id: 'clients', label: 'Clients', icon: Users },
              { id: 'finance', label: 'Finance', icon: DollarSign },
              { id: 'communication', label: 'Communication', icon: MessageSquare },
              { id: 'training', label: 'Training', icon: BookOpen },
              { id: 'goals', label: 'Goals', icon: Target },
              { id: 'import', label: 'Import Data', icon: Database }
            ].filter((tab) => isManager || tab.id === 'overview' || tab.id === 'finance' || tab.id === 'performance').map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center px-3 md:px-4 py-3 rounded-lg transition-all whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 bg-gray-50 dark:bg-gray-700/50'
                  }`}
                >
                  <Icon className="h-4 w-4 md:h-5 md:w-5 mr-1.5 md:mr-2" />
                  <span className="text-sm md:text-base">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            {/* 🔓 Sign-In — owner/manager opt-out of the PIN pad on their own device (Jose 2026-10-19). */}
            <div className="lg:col-span-3 bg-white dark:bg-gray-700 rounded-xl shadow-lg p-4 md:p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white">Sign-In</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">
                    <span className="font-semibold text-gray-700 dark:text-gray-300">Stay signed in on this device</span> — skip the PIN pad.
                    Best only on a device this barber uses alone: while it's on, anyone at the station could use their
                    signed-in session. A manual <span className="font-medium">Lock</span> is always available.
                  </p>
                  <p className="text-xs mt-2 font-medium">{(currentBarber as any)?.staySignedIn
                    ? <span className="text-emerald-600 dark:text-emerald-400">✅ PIN pad OFF for {(currentBarber as any).firstName || (currentBarber as any).name || 'this barber'}</span>
                    : <span className="text-gray-400">PIN pad ON (default)</span>}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 mt-1">
                  <input
                    type="checkbox"
                    checked={!!(currentBarber as any)?.staySignedIn}
                    onChange={(e) => handleStaySignedInToggle(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-12 h-6 bg-gray-300 dark:bg-gray-600 rounded-full peer peer-checked:bg-emerald-500 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-6"></div>
                </label>
              </div>
            </div>

            {/* 🪪 License, Locations & Access — real wizard data (Layer 1 wiring, 2026-08-02) */}
            <div className="lg:col-span-3 bg-white dark:bg-gray-700 rounded-xl shadow-lg p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-bold mb-4 text-gray-900 dark:text-white">License, Locations &amp; Access</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* License */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">License</p>
                  {currentBarber.licensePhoto ? (
                    <div className="flex items-start gap-3">
                      <img src={currentBarber.licensePhoto} alt="License" className="w-24 h-16 object-cover rounded-lg border border-gray-200 dark:border-gray-600" />
                      <div className="text-sm space-y-0.5">
                        {currentBarber.licenseNumber && <div className="text-gray-900 dark:text-white font-medium">#{currentBarber.licenseNumber}</div>}
                        {(() => {
                          if (!currentBarber.licenseExpiry) return null;
                          const exp = new Date(currentBarber.licenseExpiry);
                          const days = Math.ceil((exp.getTime() - Date.now()) / 86400000);
                          const cls = days < 0 ? 'text-red-600 dark:text-red-400' : days < 60 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400';
                          const label = days < 0 ? 'EXPIRED — needs renewal' : days < 60 ? `Renews in ${days} days` : `Valid · renews ${exp.toLocaleDateString()}`;
                          return <div className={`text-xs font-medium ${cls}`}>{label}</div>;
                        })()}
                        <a href={currentBarber.licensePhoto} target="_blank" rel="noreferrer" className="text-xs text-purple-600 dark:text-purple-400 underline">Print / Download</a>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">No license on file</p>
                  )}
                </div>
                {/* Works At */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Works At</p>
                  {currentBarber.locationIds && currentBarber.locationIds.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {currentBarber.locationIds.map(id => {
                        const loc = locations.find(l => l.id === id);
                        return <span key={id} className="text-xs px-2 py-1 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">{loc?.name || id}</span>;
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">No locations set</p>
                  )}
                </div>
                {/* Station Access / PIN */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Station Access</p>
                  <span className={`text-sm font-medium ${currentBarber.hasPin ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                    {currentBarber.hasPin ? '🔒 PIN set' : 'No PIN set'}
                  </span>
                </div>
              </div>
            </div>

            {/* Performance Summary */}
            <div className="lg:col-span-2 bg-white dark:bg-gray-700 rounded-xl shadow-lg p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-bold mb-4 md:mb-6 text-gray-900 dark:text-white">Performance Summary</h2>

              {/* This month — REAL (completed appointments this calendar month). No fabricated goal;
                  the real targets live in the Growth Track bars below. (Jose 2026-09-27) */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400">This Month · Revenue</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">${_money(_mRevenue)}</p>
                </div>
                <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400">This Month · Clients</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {_mClients}<span className="text-sm font-normal text-gray-400"> · {_mServices} svcs</span>
                  </p>
                </div>
              </div>

              {/* Specialties (real) */}
              {currentBarber.specialties && currentBarber.specialties.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold mb-3 text-gray-900 dark:text-white">Specialties</h3>
                  <div className="flex flex-wrap gap-2">
                    {currentBarber.specialties.map((specialty, idx) => (
                      <span key={idx} className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-sm">
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Growth Track — the REAL parameters tied to the weekly bonus (occupancy, rebooking,
                  returning, retail, rating). Replaced the old experience-derived skill heuristics
                  (Jose 2026-09-27). Rating reads "awaiting reviews" until the Fresha review pull lands. */}
              {(currentBarber as any).growth ? (
                <div className="space-y-4">
                  <GrowthCoachingCard growth={(currentBarber as any).growth} />
                  <GrowthTrackGoals growth={(currentBarber as any).growth} />
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 p-5 text-sm text-gray-500 dark:text-gray-400">
                  Growth metrics haven't been computed for this barber yet — run <span className="font-medium">Recompute Growth</span> (Goals tab) to populate the bars.
                </div>
              )}

              {/* Top Services */}
              <div>
                <h3 className="font-semibold mb-3 text-gray-900 dark:text-white">Top Services</h3>
                <div className="space-y-2">
                  {realTopServices.length === 0 && (
                    <p className="text-sm italic text-gray-400">No completed services yet.</p>
                  )}
                  {realTopServices.map((service, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-600 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{service.name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {service.count} completed • {service.avgDuration} min avg
                        </p>
                      </div>
                      <p className="font-bold text-green-600 dark:text-green-400">${service.revenue.toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t dark:border-gray-600">
                <h3 className="font-semibold mb-3 text-sm md:text-base text-gray-900 dark:text-white">Quick Actions</h3>
                <div className="grid grid-cols-2 gap-2 md:gap-3">
                  {/* Messages jumps to the Communication tab — manager-only (barbers don't see that tab). */}
                  {isManager && (
                  <button
                    onClick={() => setActiveTab('communication')}
                    className="px-3 md:px-4 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all flex items-center justify-center text-sm md:text-base"
                  >
                    <MessageSquare className="h-4 w-4 md:h-5 md:w-5 mr-1 md:mr-2" />
                    Messages
                  </button>
                  )}
                  <button
                    onClick={() => setShowSchedule(!showSchedule)}
                    className="px-3 md:px-4 py-2 md:py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all flex items-center justify-center text-sm md:text-base"
                  >
                    <Calendar className="h-4 w-4 md:h-5 md:w-5 mr-1 md:mr-2" />
                    Schedule
                  </button>
                  {SHOW_GAMIFICATION && (
                  <button
                    onClick={() => setShowSLIKCoins(!showSLIKCoins)}
                    className="px-3 md:px-4 py-2 md:py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-lg hover:shadow-lg transition-all flex items-center justify-center text-sm md:text-base"
                  >
                    <CreditCard className="h-4 w-4 md:h-5 md:w-5 mr-1 md:mr-2" />
                    Coins
                  </button>
                  )}
                  {/* Goals jumps to the Goals tab — manager-only (barbers don't see that tab). */}
                  {isManager && (
                  <button
                    onClick={() => setActiveTab('goals')}
                    className="px-3 md:px-4 py-2 md:py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:shadow-lg transition-all flex items-center justify-center text-sm md:text-base"
                  >
                    <Target className="h-4 w-4 md:h-5 md:w-5 mr-1 md:mr-2" />
                    Goals
                  </button>
                  )}
                </div>

                {/* Schedule View - Collapsible */}
                {showSchedule && (
                  <div className="mt-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border-2 border-purple-200 dark:border-purple-800">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-purple-900 dark:text-purple-200">Weekly Schedule</h4>
                      <button onClick={() => setShowSchedule(false)} className="text-purple-600 dark:text-purple-400 hover:text-purple-800">
                        <XCircle className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="space-y-2">
                      {Object.entries(currentBarber.workingHours).map(([day, hours]) => (
                        <div key={day} className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded-lg">
                          <span className="font-medium capitalize text-gray-900 dark:text-white">{day}</span>
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {hours.start === 'Closed' ? 'Closed' : `${hours.start} - ${hours.end}`}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-4 border-t border-purple-200 dark:border-purple-800">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Break Schedule</p>
                      {currentBarber.breakSchedule.map((breakTime, idx) => (
                        <div key={idx} className="flex items-center text-sm">
                          <Clock className="h-4 w-4 mr-2 text-purple-600 dark:text-purple-400" />
                          <span>{breakTime}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* SLIK Coins View - Collapsible — DESIGN-LATER gated */}
                {SHOW_GAMIFICATION && showSLIKCoins && (
                  <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border-2 border-yellow-200 dark:border-yellow-800">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-yellow-900 dark:text-yellow-200">SLIK Coins Balance</h4>
                      <button onClick={() => setShowSLIKCoins(false)} className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-800">
                        <XCircle className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="text-center py-6">
                      <div className="text-5xl font-bold text-yellow-600 dark:text-yellow-400 mb-2">
                        {currentBarber.digitalCoins.toLocaleString()}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Available Coins</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <div className="p-3 bg-white dark:bg-gray-700 rounded-lg text-center">
                        <p className="text-sm text-gray-600 dark:text-gray-400">Level</p>
                        <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{currentBarber.level}</p>
                      </div>
                      <div className="p-3 bg-white dark:bg-gray-700 rounded-lg text-center">
                        <p className="text-sm text-gray-600 dark:text-gray-400">Total Points</p>
                        <p className="text-2xl font-bold text-green-600 dark:text-green-400">{currentBarber.points.toLocaleString()}</p>
                      </div>
                    </div>
                    <button className="w-full mt-4 px-4 py-2 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-lg hover:shadow-lg transition-all">
                      Redeem Rewards
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Achievements & Challenges */}
            <div className="space-y-6">
              {/* Commission Structure Card */}
              <div className="bg-white dark:bg-gray-700 rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
                    Commission Structure
                  </h3>
                  {_commSliding ? (
                    <span className="px-3 py-1 bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded-full text-xs font-semibold flex items-center gap-1">
                      📈 Sliding Scale
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-full text-xs font-semibold">
                      Fixed Rate
                    </span>
                  )}
                </div>

                {_commSliding ? (
                  <div>
                    {/* Current Tier Display */}
                    <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border-2 border-green-300 dark:border-green-700">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Current Commission Rate</p>
                          <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                            {_rateStr}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600 dark:text-gray-400">{_periodLabel} Revenue</p>
                          <p className="text-lg font-bold text-gray-900 dark:text-white">
                            ${_money(_commRevenue)}
                          </p>
                        </div>
                      </div>

                      {/* Tier Progress Bar — real revenue against the shop's real tiers */}
                      {(() => {
                        const currentTier = _commCurrentTier;
                        const nextTier = _commNextTier;
                        if (!nextTier) {
                          return (
                            <p className="text-xs text-green-600 dark:text-green-400 font-semibold">
                              🎉 Top tier reached!
                            </p>
                          );
                        }
                        const span = currentTier.maxRevenue - currentTier.minRevenue;
                        const progress = span > 0 && isFinite(span)
                          ? Math.min(Math.max(((_commRevenue - currentTier.minRevenue) / span) * 100, 0), 100)
                          : 0;
                        return (
                          <div>
                            <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                              <span>${currentTier.minRevenue.toLocaleString()}</span>
                              <span>${(nextTier.minRevenue - 1).toLocaleString()}</span>
                            </div>
                            <div className="bg-gray-200 dark:bg-gray-600 rounded-full h-3 mb-2">
                              <div
                                className="bg-gradient-to-r from-green-500 to-emerald-500 h-3 rounded-full transition-all duration-500"
                                style={{ width: `${progress}%` }}
                              ></div>
                            </div>
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                              ${Math.max(nextTier.minRevenue - _commRevenue, 0).toLocaleString()} until {nextTier.rate}% tier
                            </p>
                          </div>
                        );
                      })()}
                    </div>

                    {/* All Tiers Display */}
                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Commission Tiers</h4>
                      {_commDisplayTiers.map((tier, index) => {
                        const currentTier = _commCurrentTier;
                        const isCurrentTier = tier.minRevenue === currentTier.minRevenue;

                        return (
                          <div
                            key={index}
                            className={`flex items-center justify-between p-3 rounded-lg transition-all ${
                              isCurrentTier
                                ? 'bg-gradient-to-r from-purple-100 to-indigo-100 dark:from-purple-900/40 dark:to-indigo-900/40 border-2 border-purple-400 dark:border-purple-600'
                                : 'bg-gray-50 dark:bg-gray-600 border border-gray-200 dark:border-gray-500'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                                  isCurrentTier
                                    ? 'bg-gradient-to-r from-purple-500 to-indigo-500'
                                    : 'bg-gray-400 dark:bg-gray-500'
                                }`}
                              >
                                {tier.rate}%
                              </div>
                              <div>
                                <p className={`text-xs font-medium ${isCurrentTier ? 'text-purple-900 dark:text-purple-200' : 'text-gray-700 dark:text-gray-300'}`}>
                                  Tier {index + 1} {isCurrentTier && '(Current)'}
                                </p>
                                <p className={`text-xs ${isCurrentTier ? 'text-purple-700 dark:text-purple-300' : 'text-gray-500 dark:text-gray-400'}`}>
                                  {isFinite(tier.maxRevenue)
                                    ? `$${tier.minRevenue.toLocaleString()} - $${tier.maxRevenue.toLocaleString()}`
                                    : `$${tier.minRevenue.toLocaleString()}+`}
                                </p>
                              </div>
                            </div>
                            {isCurrentTier && (
                              <div className="flex items-center gap-1 text-purple-600 dark:text-purple-400">
                                <Zap className="h-4 w-4" />
                                <span className="text-xs font-semibold">Active</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Tiers are shop-wide + managed in Settings → Commission & Pay (the single source
                        payroll reads). The old per-barber toggle wrote to a legacy field the pay
                        resolver ignored, so it's replaced with a pointer. Manager-only. (Jose 2026-09-27) */}
                    {isManager && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600 text-center">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Tiers are shop-wide — edit them in <span className="font-medium text-gray-700 dark:text-gray-300">Settings → Commission &amp; Pay</span>.
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <div className="text-5xl font-bold text-gray-900 dark:text-white mb-2">
                      {_rateStr}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Fixed Commission Rate</p>

                    {isManager && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Set a barber's pay mode (flat vs sliding scale) in <span className="font-medium text-gray-700 dark:text-gray-300">Settings → Commission &amp; Pay</span>.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* BPI Score Card — "manage" is OWNER-ONLY (Jose 2026-09-27): the modal can slide
                  money-derived params, so a barber (or manager, for now) must not open it. */}
              <div
                onClick={isOwner ? () => setShowBPIModal(true) : undefined}
                className={`bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl shadow-lg p-6 text-white transition-all duration-200 ${isOwner ? 'cursor-pointer hover:shadow-2xl hover:scale-105' : ''}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold">Barber Performance Index</h3>
                  <div className="flex items-center space-x-2">
                    {isOwner && <span className="text-sm opacity-90">Click to manage</span>}
                    <BarChart3 className="h-6 w-6" />
                  </div>
                </div>
                {(() => {
                  // BPI rework (2026-09-27): the real score from the new engine (computed real metrics +
                  // owner manual values + adjustment). Null → still loading.
                  if (!bpiResult) {
                    return <div className="text-sm opacity-80 py-6">Computing performance index…</div>;
                  }
                  const rank = bpiRank(bpiResult.final);
                  const top = [...bpiResult.contributions].filter(c => c.available).sort((a, b) => b.points - a.points).slice(0, 4);
                  return (
                    <>
                      <div className="flex items-end justify-between mb-4">
                        <div>
                          <div className="text-5xl font-bold">{bpiResult.final.toFixed(0)}</div>
                          <div className="text-sm opacity-90">out of 100</div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold">{rank.label}</div>
                          {bpiResult.adjustment !== 0 && (
                            <div className="text-xs opacity-90">incl. {bpiResult.adjustment > 0 ? '+' : ''}{bpiResult.adjustment} adj</div>
                          )}
                        </div>
                      </div>

                      <div className="bg-white/20 backdrop-blur rounded-lg p-4">
                        <div className="text-sm font-medium mb-2">Top drivers</div>
                        <div className="space-y-1.5">
                          {top.map(c => (
                            <div key={c.key} className="flex items-center justify-between text-xs">
                              <span className="opacity-90">{c.label}</span>
                              <span className="font-semibold tabular-nums">{Math.round(c.value)}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      {isOwner && <div className="text-xs opacity-90 mt-3">Tap to manage →</div>}
                    </>
                  );
                })()}
              </div>

              {/* Gamification (Achievements + Challenges) — DESIGN-LATER: hidden via SHOW_GAMIFICATION, kept for the real motivation system (2026-08-02) */}
              {SHOW_GAMIFICATION && (<>
              {/* Achievements */}
              <div className="bg-white dark:bg-gray-700 rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">Recent Achievements</h3>
                <div className="space-y-3">
                  {currentBarber.achievements.map((achievement, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <Trophy className="h-5 w-5 text-yellow-500 dark:text-yellow-400 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-sm text-gray-900 dark:text-white">{achievement.title}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{achievement.description}</p>
                        {achievement.progress && achievement.target && (
                          <div className="mt-1 bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                            <div
                              className="bg-yellow-500 h-1.5 rounded-full"
                              style={{ width: `${Math.min((achievement.progress / achievement.target) * 100, 100)}%` }}
                            ></div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Active Challenges */}
              <div className="bg-white dark:bg-gray-700 rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">Active Challenges</h3>
                <div className="space-y-3">
                  {currentBarber.activeChallenges.map((challenge, idx) => (
                    <div key={idx} className="border dark:border-gray-600 rounded-lg p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-sm text-gray-900 dark:text-white">{challenge.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {challenge.type === 'team' ? 'Team Challenge' : 'Individual'}
                          </p>
                        </div>
                        <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs">
                          {challenge.reward} coins
                        </span>
                      </div>
                      <div className="flex justify-between text-xs mb-1">
                        <span>{challenge.progress.toLocaleString()} / {challenge.target.toLocaleString()}</span>
                        <span>{Math.round((challenge.progress / challenge.target) * 100)}%</span>
                      </div>
                      <div className="bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-orange-500 to-red-500 h-2 rounded-full"
                          style={{ width: `${Math.min((challenge.progress / challenge.target) * 100, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              </>)}

              {/* Upcoming Appointments */}
              <div className="bg-white dark:bg-gray-700 rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">Today's Appointments</h3>
                <div className="space-y-2">
                  {todaysAppointments.length === 0 && (
                    <p className="text-sm italic text-gray-400">No appointments scheduled for today.</p>
                  )}
                  {todaysAppointments.map((apt, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-600 rounded">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <p className="font-medium text-sm text-gray-900 dark:text-white">{apt.clientName}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{apt.service}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-sm text-gray-900 dark:text-white">{formatTime12h(apt.time)}</p>
                        <span className="text-xs text-green-600 dark:text-green-400">{apt.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Performance Tab */}
        {activeTab === 'performance' && (() => {
          // Calculate BPI metrics for performance charts
          const bpiMetrics: BPIMetrics = {
            totalAppointments: currentBarber.ytdClients,
            totalRevenue: currentBarber.ytdRevenue,
            totalTips: currentBarber.tipsAverage * currentBarber.ytdClients,
            rebookRate: currentBarber.rebookingRate,
            averageRating: currentBarber.clientSatisfactionScore,
            punctualityScore: 95,
            employmentStartDate: new Date(Date.now() - (currentBarber.experience * 365 * 24 * 60 * 60 * 1000)),
            specialtyCertifications: currentBarber.certifications,
            teamworkScore: currentBarber.skillLevels.teamwork / 20,
            upsellRate: currentBarber.productSalesRatio || 0
          };
          const bpi = BPIService.calculateBPI(currentBarber.id, bpiMetrics);

          return (
            <BarberPerformanceCharts
              barberName={currentBarber.name}
              barberId={currentBarber.id}
              ytdRevenue={currentBarber.ytdRevenue}
              ytdClients={currentBarber.ytdClients}
              monthlyRevenue={currentBarber.monthlyRevenue}
              monthlyClients={currentBarber.monthlyClients}
              bpiScore={bpi.score}
              bpiBreakdown={{
                serviceCount: Math.round((currentBarber.ytdClients / 2500) * 100), // Normalize to 0-100
                revenue: Math.round((currentBarber.ytdRevenue / 120000) * 100), // Normalize to 0-100
                speed: currentBarber.skillLevels.speed,
                satisfaction: Math.round(currentBarber.clientSatisfactionScore * 20), // Convert 5-star to 0-100
                rebooking: currentBarber.rebookingRate
              }}
            />
          );
        })()}

        {/* Clients Tab */}
        {activeTab === 'clients' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <div className="bg-white dark:bg-gray-700 rounded-xl shadow-lg p-4 md:p-6">
              <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4">Client Overview</h3>
              <div className="grid grid-cols-2 gap-2 md:gap-4">
                <div className="text-center p-3 md:p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <Users className="h-6 w-6 md:h-8 md:w-8 text-blue-600 dark:text-blue-400 mx-auto mb-1 md:mb-2" />
                  <p className="text-xl md:text-2xl font-bold">{currentBarber.totalClients}</p>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Total</p>
                </div>
                <div className="text-center p-3 md:p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <Award className="h-6 w-6 md:h-8 md:w-8 text-purple-600 dark:text-purple-400 mx-auto mb-1 md:mb-2" />
                  <p className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">{realVipClients}</p>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">VIP</p>
                </div>
                <div className="text-center p-3 md:p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <UserCheck className="h-6 w-6 md:h-8 md:w-8 text-green-600 dark:text-green-400 mx-auto mb-1 md:mb-2" />
                  <p className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">+{realNewClients}</p>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">New</p>
                </div>
                <div className="text-center p-3 md:p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <XCircle className="h-6 w-6 md:h-8 md:w-8 text-red-600 dark:text-red-400 mx-auto mb-1 md:mb-2" />
                  <p className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">{realLostClients}</p>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Lost</p>
                </div>
              </div>
              {/* Layer 4: real from client records; ties to the People-page VIP/Model tabs */}
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-gray-400 dark:text-gray-500 italic">New = joined &lt;30d · Lost = no visit &gt;90d</p>
                {realModelClients > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 font-medium">🙌 {realModelClients} model client{realModelClients === 1 ? '' : 's'}</span>
                )}
              </div>

              <div className="mt-6">
                <h4 className="font-semibold mb-3 text-gray-900 dark:text-white">Key Metrics</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Retention Rate</span>
                    <span className="font-bold text-gray-900 dark:text-white">{currentBarber.retentionRate}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Rebooking Rate</span>
                    <span className="font-bold text-gray-900 dark:text-white">{currentBarber.rebookingRate}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">No-Show Rate</span>
                    {realNoShowRate === null
                      ? <span className="text-sm italic text-gray-400">no completed visits yet</span>
                      : <span className="font-bold text-red-600 dark:text-red-400">{realNoShowRate}%</span>}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Satisfaction Score</span>
                    <span className="font-bold text-gray-900 dark:text-white">{currentBarber.clientSatisfactionScore} ⭐</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-700 rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">Upcoming Birthdays</h3>
              <div className="space-y-3">
                {currentBarber.clientBirthdays.length === 0 && (
                  <p className="text-sm italic text-gray-400">Client birthdays aren't tracked yet.</p>
                )}
                {currentBarber.clientBirthdays.map((birthday, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-600 rounded-lg">
                    <div className="flex items-center">
                      <Gift className="h-5 w-5 text-purple-500 dark:text-purple-400 mr-3" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{birthday.clientName}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{birthday.phone}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-sm text-gray-900 dark:text-white">{birthday.date}</p>
                      <button className="text-xs text-blue-600 dark:text-blue-400 hover:underline">Send Wishes</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Finance Tab */}
        {activeTab === 'finance' && (
          <div className="space-y-4 md:space-y-6">
            {/* Growth System earnings breakdown (commission + bonus + retail spiff) — null until recompute */}
            <GrowthEarningsCard growth={(currentBarber as any).growth} commissionMode={_fullBarber?.commissionMode} commissionRate={_fullBarber?.commissionRate} />
            {/* Capacity & Projection — occupancy now + "at 80/85% you'd earn $X/yr" (needs recompute for occ data) */}
            <GrowthProjectionCard growth={(currentBarber as any).growth} />
            {/* This Pay Week — live commission-cliff: "$X more → whole check to N% = +$Y" (needs recompute) */}
            <GrowthPayWeekCard growth={(currentBarber as any).growth} barberId={(currentBarber as any).id} commissionMode={_fullBarber?.commissionMode} commissionRate={_fullBarber?.commissionRate} />
            {/* Period toggle — drives every number below, all from this barber's real completed appointments */}
            <div className="flex items-center gap-2">
              {(['today', 'this-week', 'this-month', 'this-year'] as const).map(p => (
                <button
                  key={p}
                  onClick={() => setReportPeriod(p)}
                  className={`px-3 py-1.5 rounded-lg text-xs md:text-sm font-medium touch-manipulation ${
                    reportPeriod === p
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                  }`}
                >
                  {p === 'today' ? 'Today' : p === 'this-week' ? 'This Week' : p === 'this-month' ? 'This Month' : 'This Year'}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {/* Earnings — real, computed with the SAME commission math payroll pays (close the loop) */}
              <div className="bg-white dark:bg-gray-700 rounded-xl shadow-lg p-4 md:p-6">
                <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4 text-gray-900 dark:text-white">Earnings — {_periodLabel}</h3>
                <div className="space-y-2">
                  <div className="flex justify-between"><span className="text-sm text-gray-600 dark:text-gray-400">Service revenue</span><span className="font-semibold text-gray-900 dark:text-white">${_money(periodServiceRevenue)}</span></div>
                  <div className="flex justify-between"><span className="text-sm text-gray-600 dark:text-gray-400">Product revenue</span><span className="font-semibold text-gray-900 dark:text-white">${_money(periodProductRevenue)}</span></div>
                  <div className="flex justify-between"><span className="text-sm text-gray-600 dark:text-gray-400">Tips</span><span className="font-semibold text-gray-900 dark:text-white">${_money(periodTips)}</span></div>
                  <div className="flex justify-between border-t border-gray-200 dark:border-gray-600 pt-2"><span className="text-sm text-gray-600 dark:text-gray-400">Commission ({_rateStr} svc · {_prodRate}% prod)</span><span className="font-semibold text-gray-900 dark:text-white">{_payReady ? `$${_money(periodCommission)}` : '—'}</span></div>
                  <div className="flex justify-between items-center"><span className="text-sm font-bold text-gray-900 dark:text-white">Take-home pay</span><span className="text-lg md:text-2xl font-bold text-green-600 dark:text-green-400">{_payReady ? `$${_money(periodTotalPay)}` : '—'}</span></div>
                  <p className="text-[11px] text-gray-400 pt-1">Commission + tips — same calculation as payroll.</p>
                </div>
              </div>

              {/* Services — real counts for the period */}
              <div className="bg-white dark:bg-gray-700 rounded-xl shadow-lg p-4 md:p-6">
                <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4 text-gray-900 dark:text-white">Services — {_periodLabel}</h3>
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Services performed</p>
                    <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">{periodServices}</p>
                  </div>
                  <div>
                    <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Clients served</p>
                    <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">{periodClients}</p>
                  </div>
                  <div>
                    <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Products sold</p>
                    <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">{periodProductUnits}</p>
                  </div>
                  <div>
                    <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Avg ticket</p>
                    <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">${periodAvgTicket.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-700 rounded-xl shadow-lg p-4 md:p-6">
                <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4 text-gray-900 dark:text-white">Payment Methods</h3>
                {/* Layer 3b: real cash/card/app split from checkout paymentMethod on appointments */}
                <div className="space-y-3">
                  {!realPaymentMix ? (
                    <p className="text-sm italic text-gray-400">No checkout payments recorded yet.</p>
                  ) : (
                    <>
                      <div>
                        <div className="flex justify-between mb-1"><span className="text-sm text-gray-700 dark:text-gray-200">Cash</span><span className="text-sm font-bold text-gray-900 dark:text-white">{realPaymentMix.cash}%</span></div>
                        <div className="bg-gray-200 dark:bg-gray-600 rounded-full h-2"><div className="bg-green-500 h-2 rounded-full" style={{ width: `${realPaymentMix.cash}%` }}></div></div>
                      </div>
                      <div>
                        <div className="flex justify-between mb-1"><span className="text-sm text-gray-700 dark:text-gray-200">Card</span><span className="text-sm font-bold text-gray-900 dark:text-white">{realPaymentMix.card}%</span></div>
                        <div className="bg-gray-200 dark:bg-gray-600 rounded-full h-2"><div className="bg-blue-500 h-2 rounded-full" style={{ width: `${realPaymentMix.card}%` }}></div></div>
                      </div>
                      <div>
                        <div className="flex justify-between mb-1"><span className="text-sm text-gray-700 dark:text-gray-200">App / Other</span><span className="text-sm font-bold text-gray-900 dark:text-white">{realPaymentMix.app}%</span></div>
                        <div className="bg-gray-200 dark:bg-gray-600 rounded-full h-2"><div className="bg-purple-500 h-2 rounded-full" style={{ width: `${realPaymentMix.app}%` }}></div></div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Product Sales — REAL per-product for the period (from the retail loop's productsSold[]) */}
              <div className="bg-white dark:bg-gray-700 rounded-xl shadow-lg p-4 md:p-6 lg:col-span-3">
                <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4 text-gray-900 dark:text-white">Product Sales — {_periodLabel}</h3>
                <div className="space-y-3">
                  {periodProductLines.length === 0 && (
                    <p className="text-sm italic text-gray-400">No products sold in this period.</p>
                  )}
                  {periodProductLines.map((product, idx) => (
                    <div key={idx} className="flex justify-between">
                      <div>
                        <p className="font-medium text-sm text-gray-900 dark:text-white">{product.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{product.quantity} sold</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-gray-900 dark:text-white">${product.revenue.toFixed(2)}</p>
                        <p className="text-xs text-green-600 dark:text-green-400">+${product.commission.toFixed(2)} commission</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* TODAY'S WORK — the actual checkouts for the period, newest first. The "what did I do
                  today" list Jose asked for: service (+ products), client, time, amount, tip. Defaults
                  to Today (period toggle above). (Jose 2026-08-13) */}
              <div className="bg-white dark:bg-gray-700 rounded-xl shadow-lg p-4 md:p-6 lg:col-span-3">
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <h3 className="text-base md:text-lg font-bold text-gray-900 dark:text-white">
                    {reportPeriod === 'today' ? "Today's Work" : `Checkouts — ${_periodLabel}`}
                  </h3>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {_worklog.length} checkout{_worklog.length !== 1 ? 's' : ''} · ${_money(periodTotalRevenue)}
                  </span>
                </div>
                <div className="space-y-1 max-h-96 overflow-y-auto">
                  {_worklog.length === 0 && (
                    <p className="text-sm italic text-gray-400">No completed checkouts {reportPeriod === 'today' ? 'yet today' : 'in this period'}.</p>
                  )}
                  {_worklog.map((a, idx) => {
                    const prodTotal = (a.productsSold || []).reduce((s: number, p: any) => s + (p.total || 0), 0);
                    const prodCount = (a.productsSold || []).reduce((s: number, p: any) => s + (p.quantity || 1), 0);
                    const rowTotal = (a.price || 0) + prodTotal;
                    const t = String(a.time || '');
                    const [hh, mm] = t.split(':');
                    const h = parseInt(hh, 10);
                    const time12 = isNaN(h) ? t : `${(h % 12) || 12}:${mm || '00'} ${h < 12 ? 'AM' : 'PM'}`;
                    return (
                      <div key={a.id || idx} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-600 last:border-0">
                        <div className="min-w-0">
                          <p className="font-medium text-sm text-gray-900 dark:text-white truncate">
                            {(a as any).serviceName || a.service || 'Sale'}
                            {prodCount > 0 && <span className="text-purple-500 dark:text-purple-400"> · 🛍 {prodCount}</span>}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{time12} · {a.clientName || 'Walk-in'}</p>
                        </div>
                        <div className="text-right shrink-0 ml-3">
                          <p className="font-bold text-gray-900 dark:text-white">${_money(rowTotal)}</p>
                          {(a.tipAmount || 0) > 0 && <p className="text-xs text-green-600 dark:text-green-400">+${_money(a.tipAmount || 0)} tip</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Communication Tab */}
        {activeTab === 'communication' && (
          <CommunicationCenter
            barberId={currentBarber.id}
            barberName={currentBarber.name}
          />
        )}

        {/* Training Tab */}
        {activeTab === 'training' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <div className="bg-white dark:bg-gray-700 rounded-xl shadow-lg p-4 md:p-6">
              <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4 text-gray-900 dark:text-white">Certifications</h3>
              <div className="space-y-2 md:space-y-3">
                {currentBarber.certifications.map((cert, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 md:p-3 bg-gray-50 dark:bg-gray-600 rounded-lg">
                    <div className="flex items-center">
                      <Shield className="h-4 w-4 md:h-5 md:w-5 text-green-500 dark:text-green-400 mr-2 md:mr-3 flex-shrink-0" />
                      <span className="font-medium text-sm md:text-base text-gray-900 dark:text-white">{cert}</span>
                    </div>
                    <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-green-500 dark:text-green-400 flex-shrink-0" />
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-700 rounded-xl shadow-lg p-4 md:p-6">
              <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4 text-gray-900 dark:text-white">Completed Training</h3>
              <div className="space-y-2 md:space-y-3">
                {currentBarber.trainingCompleted.map((training, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 md:p-3 bg-gray-50 dark:bg-gray-600 rounded-lg">
                    <div className="flex items-center">
                      <BookOpen className="h-4 w-4 md:h-5 md:w-5 text-blue-500 dark:text-blue-400 mr-2 md:mr-3 flex-shrink-0" />
                      <span className="font-medium text-sm md:text-base text-gray-900 dark:text-white">{training}</span>
                    </div>
                    <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">Completed</span>
                  </div>
                ))}
              </div>
              <button className="mt-3 md:mt-4 w-full px-3 md:px-4 py-2 bg-gradient-to-r from-indigo-400 to-indigo-600 text-white rounded-lg hover:shadow-lg transition-all text-sm md:text-base">
                Browse Training Catalog
              </button>
            </div>
          </div>
        )}

        {/* Goals Tab */}
        {activeTab === 'goals' && (
          <div className="space-y-5">
            {/* Growth System: the Growth Track components ARE the auto-tracked goals + coaching focus. */}
            <GrowthTrackGoals growth={(currentBarber as any).growth} />
            <GrowthCoachingCard growth={(currentBarber as any).growth} />
            <BarberGoalsManagement
              barberId={currentBarber.id}
              barberName={currentBarber.name}
              userRole={{
                role: 'admin',
                permissions: {
                  canViewGoals: true,
                  canEditGoals: true,
                  canCreateGoals: true,
                  canDeleteGoals: true,
                  canViewAllBarbers: true
                }
              }}
            />
          </div>
        )}

        {/* Import Data Tab */}
        {activeTab === 'import' && (
          <div className="max-w-4xl mx-auto">
            <div className="mb-4 md:mb-6">
              <h2 className="text-xl md:text-2xl font-bold mb-2 text-gray-900 dark:text-white">Import Staff Data</h2>
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">
                Quickly import your existing staff members from a CSV file. Download the template, fill it with your barber data, and upload to migrate seamlessly.
              </p>
            </div>

            {/* Alert */}
            <div className="mb-4 md:mb-6 p-3 md:p-4 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded">
              <div className="flex items-start gap-2 md:gap-3">
                <AlertCircle className="h-4 w-4 md:h-5 md:w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs md:text-sm">
                  <p className="font-medium text-blue-900 dark:text-blue-200 mb-1">
                    Import Best Practices:
                  </p>
                  <ul className="text-blue-800 dark:text-blue-300 space-y-1 ml-3 md:ml-4 list-disc">
                    <li>Download the template to see required fields and format</li>
                    <li>Include all barber details: name, email, phone, skills, certifications</li>
                    <li>For large teams, import in batches of 50-100 barbers</li>
                    <li>Review the results and fix any errors before importing more data</li>
                  </ul>
                </div>
              </div>
            </div>

            <CSVImportCard
              type="barber"
              title="Import Barbers"
              description="Upload your staff roster with skills, availability, and performance data"
              icon={<Users className="h-6 w-6 text-purple-600 dark:text-purple-400" />}
              onImportComplete={async () => {
                toast.success('Barber import completed! Refreshing data...');
                // Reload barbers
                const barbersData = await barberDataService.getAllBarbers();
                if (barbersData.length > 0) {
                  const mappedBarbers = barbersData.map(b => ({
                    ...defaultBarbers[0],
                    id: b.id,
                    name: `${b.firstName} ${b.lastName}`,
                    email: b.email,
                    phone: b.phone,
                    photo: b.photo,
                    role: b.careerStage as any,
                    experience: b.experience || 8,
                    status: b.status as any || 'active',
                    monthlyRevenue: b.monthlyRevenue || 0,
                    ytdRevenue: b.monthlyRevenue * 9 || 0,
                    monthlyClients: Math.round(b.totalClients / 9) || 0,
                    ytdClients: b.totalClients || 0,
                  }));
                  setBarbers(mappedBarbers);
                  setActiveTab('overview');
                }
              }}
            />
          </div>
        )}
      </div>

      {/* Barber Selection (Bottom Bar) */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-700 shadow-lg border-t dark:border-gray-600 pb-safe">
        <div className="max-w-7xl mx-auto px-3 md:px-6 py-2 md:py-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-0">
            <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400 hidden md:block">Switch Barber Profile:</p>
            <div className="flex gap-1.5 md:gap-2 overflow-x-auto pb-1 md:pb-0 scrollbar-hide">
              {barbers.map((barber, idx) => (
                <button
                  key={barber.id}
                  onClick={() => setSelectedBarber(idx)}
                  className={`px-2.5 md:px-4 py-1.5 md:py-2 rounded-lg transition-all text-xs md:text-sm whitespace-nowrap flex-shrink-0 ${
                    selectedBarber === idx
                      ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-lg'
                      : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:shadow'
                  }`}
                >
                  {barber.name}
                </button>
              ))}
              <button
                onClick={() => navigate('/barber-reports/' + currentBarber.id)}
                className="px-2.5 md:px-4 py-1.5 md:py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:shadow-lg transition-all flex items-center text-xs md:text-sm whitespace-nowrap flex-shrink-0"
              >
                <BarChart3 className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                Analytics
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* BPI Management Modal — owner-only (rework 2026-09-27: computed read-only + manual sliders) */}
      {showBPIModal && isOwner && (
        <BPIManagementModal
          barberId={currentBarber.id}
          barberName={currentBarber.name}
          isOwner={isOwner}
          onClose={() => setShowBPIModal(false)}
          onSave={() => {
            setShowBPIModal(false);
            setBpiRefresh(n => n + 1); // recompute the card with the saved values
          }}
        />
      )}
    </div>
  );
};

export default ComprehensiveBarberProfile;